import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { api } from '../lib/api';
import { getCurrentDomain } from '../lib/domain';
import {
  Link2,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  AlertCircle,
  ArrowRight,
  CheckCircle,
  KeyRound,
  UserCheck,
  Sparkles,
} from 'lucide-react';

interface SetupPasswordPageProps {
  onSuccess: () => void;
  onNavigateLogin: () => void;
}

interface InvitedStaffUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

export const SetupPasswordPage: React.FC<SetupPasswordPageProps> = ({
  onSuccess,
  onNavigateLogin,
}) => {
  const { completeInviteLogin } = useAuth();
  const { showToast } = useToast();

  const [token, setToken] = useState<string>('');
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [invitedUser, setInvitedUser] = useState<InvitedStaffUser | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Extract and verify invitation token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryToken = params.get('token');

    if (!queryToken || !queryToken.trim()) {
      setValidationError(
        'No invitation token provided. Please check the full invitation link sent to you by your administrator.'
      );
      setIsValidating(false);
      return;
    }

    const cleanToken = queryToken.trim();
    setToken(cleanToken);

    const verifyToken = async () => {
      try {
        setIsValidating(true);
        setValidationError(null);
        const res = await api.get<{
          valid: boolean;
          user: InvitedStaffUser;
          expiresAt: string;
        }>(`/api/auth/verify-invite?token=${encodeURIComponent(cleanToken)}`);

        if (res && res.valid && res.user) {
          setInvitedUser(res.user);
        } else {
          setValidationError('Invalid invitation link. Please request a new invite from an administrator.');
        }
      } catch (err: any) {
        setValidationError(
          err.message || 'The invitation link is expired or invalid. Please request a fresh invitation link.'
        );
      } finally {
        setIsValidating(false);
      }
    };

    verifyToken();
  }, []);

  const isMinLength = password.length >= 8;
  const isMatching = password.length > 0 && password === confirmPassword;
  const canSubmit = isMinLength && isMatching && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!isMinLength) {
      setSubmitError('Password must be at least 8 characters long.');
      return;
    }

    if (!isMatching) {
      setSubmitError('Passwords do not match. Please verify both fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await api.post<{
        success: boolean;
        token: string;
        user: any;
      }>('/api/auth/setup-password', {
        token,
        password,
      });

      if (res && res.success && res.token) {
        completeInviteLogin(res.token, res.user);
        showToast('Account Activated', `Welcome to Brototype, ${res.user.name}!`, 'success');
        onSuccess();
      } else {
        setSubmitError('Could not set up password. Please try again or request a new link.');
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to complete password setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090d16] text-slate-100 flex flex-col justify-between selection:bg-orange-500 selection:text-white relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-gradient-to-tr from-orange-600/15 via-amber-500/10 to-transparent blur-[120px] pointer-events-none rounded-full" />

      {/* Header */}
      <header className="border-b border-slate-800/80 bg-slate-950/60 backdrop-blur-md relative z-10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-orange-600 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-950/40 border border-orange-400/30 group-hover:scale-105 transition-transform">
              <Link2 className="w-4 h-4 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-slate-100 tracking-tight text-sm">
                BROTOTYPE
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 font-semibold border border-orange-500/30">
                LNK
              </span>
            </div>
          </a>

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Secure Staff Onboarding</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
        <div className="max-w-md w-full space-y-5">
          {/* Loading State */}
          {isValidating && (
            <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl p-8 text-center space-y-4 shadow-2xl">
              <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-400 mx-auto flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-bold text-white">Validating Invitation</h2>
                <p className="text-xs text-slate-400">
                  Verifying your invitation token with Brototype edge security...
                </p>
              </div>
            </Card>
          )}

          {/* Invalid / Expired Token State */}
          {!isValidating && validationError && (
            <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl p-6 space-y-5 shadow-2xl text-center">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 mx-auto flex items-center justify-center">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-xl font-extrabold text-white">Invitation Unavailable</h2>
                <p className="text-xs text-slate-400 leading-relaxed max-w-sm mx-auto">
                  {validationError}
                </p>
              </div>
              <div className="pt-2 flex flex-col sm:flex-row gap-2 justify-center">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full justify-center"
                  onClick={onNavigateLogin}
                >
                  Go to Staff Login
                </Button>
              </div>
            </Card>
          )}

          {/* Valid Token: Password Setup Form */}
          {!isValidating && !validationError && invitedUser && (
            <>
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-400 mb-2">
                  <KeyRound className="w-6 h-6" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Welcome, {invitedUser.name}
                </h1>
                <p className="text-xs sm:text-sm text-slate-400 max-w-sm mx-auto">
                  Set your account password to complete your onboarding to the Brototype Link Manager.
                </p>
              </div>

              <Card className="border-slate-800 bg-slate-900/80 backdrop-blur-xl shadow-2xl">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Staff Info Banner */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/70 border border-slate-800 text-xs">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-orange-400 shrink-0">
                      <UserCheck className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-200 font-semibold truncate">{invitedUser.name}</p>
                      <p className="text-slate-400 text-[11px] font-mono truncate">{invitedUser.email}</p>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 capitalize shrink-0">
                      {invitedUser.role.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Submit Error */}
                  {submitError && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs animate-in fade-in">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{submitError}</span>
                    </div>
                  )}

                  {/* New Password */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Create Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-slate-950/70 border border-slate-800 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500/60 transition-all font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-slate-950/70 border border-slate-800 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500/60 transition-all font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 cursor-pointer"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Password Requirements Checklist */}
                  <div className="space-y-1.5 pt-1 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle
                        className={`w-3.5 h-3.5 transition-colors ${
                          isMinLength ? 'text-emerald-400' : 'text-slate-600'
                        }`}
                      />
                      <span className={isMinLength ? 'text-slate-300' : 'text-slate-500'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle
                        className={`w-3.5 h-3.5 transition-colors ${
                          isMatching ? 'text-emerald-400' : 'text-slate-600'
                        }`}
                      />
                      <span className={isMatching ? 'text-slate-300' : 'text-slate-500'}>
                        Passwords match
                      </span>
                    </div>
                  </div>

                  {/* Submit CTA */}
                  <div className="pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full justify-center"
                      size="lg"
                      isLoading={isSubmitting}
                      disabled={!canSubmit}
                      rightIcon={<ArrowRight className="w-4 h-4" />}
                    >
                      Complete Setup & Enter Dashboard
                    </Button>
                  </div>
                </form>
              </Card>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 py-4 text-center text-xs text-slate-500 relative z-10">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>&copy; {new Date().getFullYear()} Brototype. All rights reserved.</span>
          <span className="font-mono text-[11px] text-slate-600">
            {getCurrentDomain()} &bull; PBKDF2 Web Crypto Protected
          </span>
        </div>
      </footer>
    </div>
  );
};

export default SetupPasswordPage;
